// Turn a release-tracker record's `error` payload into something readable.
//
// cd-pipeline-infra writes `error` as `{Error, Cause}` (capitalised keys —
// cd-deployer/internal/store/releasetracker ErrorDetail). `Error` is a short
// title from a fixed vocabulary (cli/exit.go); `Cause` arrives in one of a
// handful of shapes depending on which engine failed:
//
//   - Argo step: a Go error chain `a: b: c`, capped at 2 KB and suffixed with
//     ` …[truncated]` when cut (cli/exit.go terminationMessage).
//   - Argo node fallback: `<node display name>: <argo message>`, or
//     `workflow status: "<raw>"` when no node failed (cli/failures.go).
//   - Step Functions, already unwrapped to plain text.
//   - Step Functions Lambda failure: a JSON string {errorMessage, errorType, stackTrace}.
//   - CodeBuild failure: a JSON string of the CodeBuild Build object.
//   - A nested `{Error, Cause}` JSON string the ASL did not unwrap.
//   - Placeholders: "Unknown error" / "Unknown cause" (postdeploy/record.go), or empty.
//
// Older records also carry `{error, cause}`, `{message}`, `{errorMessage}` or a
// plain string. Everything shown is first cleaned of ANSI escapes and tofu-plan
// JSON dumps. Nothing here throws: a payload it cannot make sense of still
// produces a generic view.

import { stripAnsi } from './ansi';
import { hiddenPlaceholder, isJsonDump, redactJsonDumps } from './logFormat';

export type ErrorKind =
  | 'terraform-plan'
  | 'terraform-apply'
  | 'policy'
  | 'rejected'
  | 'review-timeout'
  | 'launch'
  | 'pipeline'
  | 'generic';

export interface ErrorView {
  /** The `Error` value; omitted when absent or the "Unknown error" placeholder. */
  title?: string;
  /** One line saying what went wrong. Never empty. */
  summary: string;
  /** Full multi-line text for a details toggle; omitted when it would repeat summary. */
  details?: string;
  /** Category mapped from the Error vocabulary. */
  kind: ErrorKind;
  /** The cause ended with the deployer's truncation suffix (removed from the text). */
  truncated: boolean;
  /** CodeBuild Logs.DeepLink, when present and http(s). */
  link?: { label: string; href: string };
}

/** Summary used when the cause is empty or a placeholder. */
export const NO_DETAIL = 'No further detail was recorded';
/** Summary used for JSON with no recognisable message field. */
export const UNRECOGNISED_PAYLOAD = 'Unrecognised error payload';
/** Cap on `details`, to keep a pathological payload from building a huge DOM. */
export const DETAILS_MAX_CHARS = 20_000;
/** Longest summary before it is cut (the full text then goes to details). */
const SUMMARY_MAX_CHARS = 300;

/** cd-deployer cli/exit.go truncationSuffix. */
const TRUNCATION_SUFFIX = '…[truncated]';

const UNKNOWN_ERROR = 'unknown error';
const PLACEHOLDER_CAUSES = new Set(['', 'unknown cause', 'null', 'undefined', 'none']);

const KIND_BY_TITLE: Record<string, ErrorKind> = {
  'terraform plan failed.': 'terraform-plan',
  'terraform plan failed': 'terraform-plan',
  'terraform apply failed.': 'terraform-apply',
  'terraform apply failed': 'terraform-apply',
  'opa policy violations found': 'policy',
  'terraform plan rejected': 'rejected',
  'states.timeout': 'review-timeout',
  'terraform plan review never opened': 'review-timeout',
  launchfailed: 'launch',
  pipelinefailed: 'pipeline',
};

type Json = Record<string, unknown>;

const isObject = (v: unknown): v is Json =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** Remove ANSI escapes and tofu-plan / large JSON dump lines. */
const clean = (text: string): string => redactJsonDumps(stripAnsi(text));

const capDetails = (text: string): string =>
  text.length > DETAILS_MAX_CHARS
    ? `${text.slice(0, DETAILS_MAX_CHARS)}\n… (${text.length - DETAILS_MAX_CHARS} more characters not shown)`
    : text;

/** JSON.stringify that survives cycles and BigInts. */
const safeStringify = (value: unknown, indent?: number): string => {
  const seen = new WeakSet<object>();
  try {
    return (
      JSON.stringify(
        value,
        (_key, v: unknown) => {
          if (typeof v === 'bigint') return v.toString();
          if (typeof v === 'object' && v !== null) {
            if (seen.has(v)) return '[Circular]';
            seen.add(v);
          }
          return v;
        },
        indent
      ) ?? String(value)
    );
  } catch {
    try {
      return String(value);
    } catch {
      return '';
    }
  }
};

/** Parse `text` as a JSON object/array; undefined for anything else. */
const parseJson = (text: string): unknown => {
  const t = text.trim();
  if (!(t.startsWith('{') && t.endsWith('}')) && !(t.startsWith('[') && t.endsWith(']'))) {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(t);
    return typeof parsed === 'object' && parsed !== null ? parsed : undefined;
  } catch {
    return undefined;
  }
};

/** First non-empty string among `keys` on `obj`. */
const pickString = (obj: Json, keys: string[]): string | undefined => {
  for (const key of keys) {
    const v = obj[key];
    if (typeof v === 'string' && v.trim()) return v;
  }
  return undefined;
};

const hasAnyKey = (obj: Json, keys: string[]): boolean => keys.some(k => k in obj);

const TITLE_KEYS = ['Error', 'error'];
const CAUSE_KEYS = ['Cause', 'cause'];
const MESSAGE_KEYS = [
  'message',
  'Message',
  'errorMessage',
  'msg',
  'reason',
  'detail',
  'error',
  'Error',
];

const kindOf = (title?: string): ErrorKind =>
  (title && KIND_BY_TITLE[title.trim().toLowerCase()]) || 'generic';

const cleanTitle = (raw: unknown): string | undefined => {
  if (typeof raw !== 'string') return undefined;
  const t = stripAnsi(raw).trim();
  if (!t || t.toLowerCase() === UNKNOWN_ERROR) return undefined;
  return t;
};

/** The parts of a cause once shaped, before the title/kind are attached. */
interface CauseView {
  summary: string;
  details?: string;
  link?: ErrorView['link'];
  /** Title found inside the cause (a nested {Error, Cause}). */
  innerTitle?: string;
}

/** Summary + details for a single line or block of text, capping the summary. */
const fromLines = (summary: string, details?: string): CauseView => {
  let s = summary.trim();
  let d = details?.trim();
  if (s.length > SUMMARY_MAX_CHARS) {
    d = d ?? s;
    s = `${s.slice(0, SUMMARY_MAX_CHARS)}…`;
  }
  if (!s) s = NO_DETAIL;
  return { summary: s, details: d && d !== s ? d : undefined };
};

/**
 * Split a Go error chain on ": " (colon + space only, so URLs and timestamps
 * survive), ignoring separators inside double-quoted spans.
 */
export const splitChain = (text: string): string[] => {
  const out: string[] = [];
  let inQuote = false;
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"' && text[i - 1] !== '\\') inQuote = !inQuote;
    else if (!inQuote && ch === ':' && text[i + 1] === ' ') {
      out.push(text.slice(start, i));
      start = i + 2;
      i++;
    }
  }
  out.push(text.slice(start));
  return out.map(s => s.trim()).filter(Boolean);
};

const WORKFLOW_STATUS_RE = /^workflow status: "?([^"]*)"?$/i;
const EXIT_STATUS_RE = /^exit status \d+$/i;

const describeText = (text: string): CauseView => {
  const t = text.trim();
  if (PLACEHOLDER_CAUSES.has(t.toLowerCase())) return { summary: NO_DETAIL };

  const status = WORKFLOW_STATUS_RE.exec(t);
  if (status) {
    const raw = status[1].trim() || 'unknown';
    return {
      summary: `Workflow ended with status "${raw}" before any step reported an error`,
      details: t,
    };
  }

  if (t.includes('\n')) {
    // Multi-line output (terraform diagnostics, a panic): first line up top,
    // the whole text behind the toggle.
    const first = t.split('\n').find(l => l.trim()) ?? '';
    return fromLines(first, t);
  }

  const segments = splitChain(t);
  if (segments.length < 2) return fromLines(t);
  // The root cause is the last segment. A bare "exit status N" says nothing
  // on its own, so keep the segment that names what exited.
  const last = segments[segments.length - 1];
  const summary =
    EXIT_STATUS_RE.test(last) && segments.length >= 2
      ? `${segments[segments.length - 2]}: ${last}`
      : last;
  // The summary already says everything: a toggle would only re-split it.
  if (summary === t) return fromLines(t);
  const details = segments.map((seg, depth) => `${'  '.repeat(depth)}${seg}`).join('\n');
  return fromLines(summary, details);
};

const isLambdaPayload = (obj: Json): boolean =>
  typeof obj.errorMessage === 'string' ||
  (typeof obj.errorType === 'string' && 'stackTrace' in obj);

const describeLambda = (obj: Json): CauseView => {
  const message = clean(typeof obj.errorMessage === 'string' ? obj.errorMessage : '').trim();
  const type = typeof obj.errorType === 'string' ? stripAnsi(obj.errorType).trim() : '';
  const [firstLine = '', ...rest] = message.split('\n');
  let summary = type && firstLine ? `${type}: ${firstLine}` : firstLine || type;
  const detailParts: string[] = [];
  if (rest.length) detailParts.push(message);
  if (Array.isArray(obj.stackTrace)) {
    const stack = obj.stackTrace
      .map(frame => (typeof frame === 'string' ? frame : safeStringify(frame)))
      .map(frame => clean(frame).replace(/\s+$/, ''))
      .filter(Boolean)
      .join('\n');
    if (stack) detailParts.push(stack);
  }
  if (!summary) summary = NO_DETAIL;
  return fromLines(summary, detailParts.length ? detailParts.join('\n\n') : undefined);
};

const codeBuildOf = (obj: Json): Json | undefined => {
  if (isObject(obj.Build)) return obj.Build;
  if (Array.isArray(obj.Phases) && ('BuildStatus' in obj || 'Arn' in obj || 'Logs' in obj))
    return obj;
  return undefined;
};

const FAILED_PHASE_STATUSES = ['FAILED', 'FAULT', 'TIMED_OUT', 'STOPPED'];

const phaseMessages = (phase: Json): string[] =>
  (Array.isArray(phase.Contexts) ? phase.Contexts : [])
    .map(c => (isObject(c) && typeof c.Message === 'string' ? clean(c.Message).trim() : ''))
    .filter(Boolean);

const describeCodeBuild = (build: Json): CauseView => {
  const phases = (Array.isArray(build.Phases) ? build.Phases : []).filter(isObject);
  const failed =
    phases.find(p => p.PhaseStatus === 'FAILED') ??
    phases.find(
      p => typeof p.PhaseStatus === 'string' && FAILED_PHASE_STATUSES.includes(p.PhaseStatus)
    );

  let summary: string;
  if (failed) {
    const type = typeof failed.PhaseType === 'string' ? failed.PhaseType : 'Phase';
    const msgs = phaseMessages(failed);
    const verb =
      failed.PhaseStatus === 'FAILED' ? 'failed' : String(failed.PhaseStatus).toLowerCase();
    summary = msgs.length ? `${type} ${verb}: ${msgs.join('; ')}` : `${type} ${verb}`;
  } else if (typeof build.BuildStatus === 'string' && build.BuildStatus) {
    summary = `Build ${build.BuildStatus}`;
  } else {
    summary = 'CodeBuild build failed';
  }

  const lines: string[] = [];
  if (typeof build.Id === 'string') lines.push(`Build ${build.Id}`);
  if (typeof build.BuildStatus === 'string') lines.push(`Status: ${build.BuildStatus}`);
  phases.forEach(p => {
    const type = typeof p.PhaseType === 'string' ? p.PhaseType : '?';
    const status = typeof p.PhaseStatus === 'string' ? p.PhaseStatus : '—';
    lines.push(`${type}: ${status}`);
    phaseMessages(p).forEach(m => lines.push(`  ${m}`));
  });

  const view = fromLines(summary, lines.length ? lines.join('\n') : undefined);
  const logs = isObject(build.Logs) ? build.Logs : undefined;
  const href = logs && typeof logs.DeepLink === 'string' ? logs.DeepLink.trim() : '';
  if (/^https?:\/\//i.test(href)) view.link = { label: 'CodeBuild logs', href };
  return view;
};

/** Describe a parsed JSON cause. `raw` is its source text, when it had one. */
const describeJson = (value: unknown, depth: number, raw?: string): CauseView => {
  if (isObject(value)) {
    // A nested {Error, Cause} the ASL left wrapped: unwrap once.
    if (depth === 0 && hasAnyKey(value, CAUSE_KEYS)) {
      const inner = describeCause(
        CAUSE_KEYS.map(k => value[k]).find(v => v != null),
        depth + 1
      );
      const innerTitle = cleanTitle(pickString(value, TITLE_KEYS));
      return { ...inner, innerTitle: inner.innerTitle ?? innerTitle };
    }
    if (isLambdaPayload(value)) return describeLambda(value);
    const build = codeBuildOf(value);
    if (build) return describeCodeBuild(build);
  }

  const source = raw ?? safeStringify(value);
  if (isJsonDump(source)) return { summary: hiddenPlaceholder(source.length) };

  const message = isObject(value) ? pickString(value, MESSAGE_KEYS) : undefined;
  const pretty = capDetails(clean(safeStringify(value, 2)));
  if (message) {
    const view = describeText(clean(message));
    return {
      ...view,
      details: view.details ? `${view.details}\n\n${pretty}` : pretty,
    };
  }
  return { summary: UNRECOGNISED_PAYLOAD, details: pretty };
};

function describeCause(cause: unknown, depth: number): CauseView {
  if (cause == null) return { summary: NO_DETAIL };
  if (typeof cause === 'string') {
    const stripped = stripAnsi(cause);
    const parsed = parseJson(stripped);
    if (parsed !== undefined) return describeJson(parsed, depth, stripped);
    return describeText(redactJsonDumps(stripped));
  }
  if (typeof cause === 'number' || typeof cause === 'boolean' || typeof cause === 'bigint') {
    return describeText(String(cause));
  }
  if (typeof cause === 'object') return describeJson(cause, depth);
  return describeText(safeStringify(cause));
}

/** Strip the deployer's truncation suffix from a string cause. */
const stripTruncation = (cause: unknown): { cause: unknown; truncated: boolean } => {
  if (typeof cause !== 'string') return { cause, truncated: false };
  const trimmed = cause.trimEnd();
  if (!trimmed.endsWith(TRUNCATION_SUFFIX)) return { cause, truncated: false };
  return {
    cause: trimmed.slice(0, -TRUNCATION_SUFFIX.length).trimEnd(),
    truncated: true,
  };
};

/** Split the raw payload into a title and a cause. */
const normalise = (error: unknown): { title?: string; cause: unknown } | null => {
  if (error == null) return null;
  if (typeof error === 'string') {
    if (!error.trim()) return null;
    const parsed = parseJson(stripAnsi(error));
    if (isObject(parsed)) return normalise(parsed);
    return { cause: error };
  }
  if (!isObject(error)) return { cause: error };

  const titleValue = TITLE_KEYS.map(k => error[k]).find(v => v != null);
  const hasCause = hasAnyKey(error, CAUSE_KEYS);
  const causeValue = CAUSE_KEYS.map(k => error[k]).find(v => v != null);

  if (hasCause || titleValue !== undefined) {
    // {error: {...}}: an older record nesting the real payload.
    if (!hasCause && titleValue !== undefined && typeof titleValue !== 'string') {
      return normalise(titleValue);
    }
    const title = typeof titleValue === 'string' ? titleValue : undefined;
    // A lone non-vocabulary `error` string is the message, not a title.
    if (
      !hasCause &&
      title &&
      kindOf(title) === 'generic' &&
      title.trim().toLowerCase() !== UNKNOWN_ERROR
    ) {
      return { cause: title };
    }
    return { title, cause: causeValue };
  }

  if (isLambdaPayload(error) || codeBuildOf(error)) return { cause: error };
  const message = pickString(error, MESSAGE_KEYS);
  if (message) return { cause: message };
  return { cause: error };
};

/**
 * Describe a record's `error` payload for display. Null when there is no error
 * at all. Never throws.
 */
export function describeError(error: unknown): ErrorView | null {
  try {
    const parts = normalise(error);
    if (!parts) return null;
    const { cause, truncated } = stripTruncation(parts.cause);
    const view = describeCause(cause, 0);
    const title = cleanTitle(parts.title) ?? view.innerTitle;
    const out: ErrorView = {
      summary: view.summary || NO_DETAIL,
      kind: kindOf(title),
      truncated,
    };
    if (title) out.title = title;
    if (view.details && view.details !== out.summary) out.details = capDetails(view.details);
    if (view.link) out.link = view.link;
    return out;
  } catch {
    return { summary: UNRECOGNISED_PAYLOAD, kind: 'generic', truncated: false };
  }
}

/** Plain-text form for copy-to-clipboard: "title — summary\n\ndetails". */
export function errorToPlainText(view: ErrorView): string {
  const head = view.title ? `${view.title} — ${view.summary}` : view.summary;
  return view.details ? `${head}\n\n${view.details}` : head;
}

/** A record's `error_info` string, cleaned for display ('' when absent). */
export function describeErrorInfo(text: unknown): string {
  try {
    if (typeof text === 'number' || typeof text === 'boolean') return String(text);
    if (typeof text !== 'string') return '';
    return capDetails(clean(text).trim());
  } catch {
    return '';
  }
}
